import React, { useState, useEffect, useRef } from 'react';
import { 
  Mail, Save, RotateCcw, ChevronDown, Plus, Trash2, 
  Eye, Languages, CheckCircle, AlertCircle, Loader2, Upload, Image, X 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import { toast } from 'sonner';

/** E-posta logosu: statikler FastAPI kökünde; prod'da genelde sadece /api proxy'lenir → dosyalar /api/uploads/... */
const getEmailLogoDisplayUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const origin = (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  if (path.startsWith('/api/')) {
    return `${origin}${path}`;
  }
  if (path.startsWith('/uploads/')) {
    return `${origin}/api${path}`;
  }
  return `${origin}${path}`;
};

const LANGUAGE_NAMES = {
  tr: 'Türkçe',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  ar: 'العربية',
  ru: 'Русский',
  fa: 'فارسی'
};

const EmailTemplateEditor = () => {
  const [templateType, setTemplateType] = useState('restaurant');
  const [selectedLang, setSelectedLang] = useState('tr');
  const [templates, setTemplates] = useState({});
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  
  // Logo state
  const [logoUrl, setLogoUrl] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);
  const [isCustomLogo, setIsCustomLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  // Current template being edited
  const [currentTemplate, setCurrentTemplate] = useState({
    subject: '',
    greeting: '',
    intro: '',
    slogan1: '',
    slogan2: '',
    benefits_title: '',
    benefits: [],
    cta_button: '',
    video_text: '',
    contact_text: '',
    contact_email: '',
    footer: '',
    show_video: true
  });

  useEffect(() => {
    fetchTemplates();
    fetchLogo();
  }, [templateType]);

  useEffect(() => {
    if (templates[selectedLang]) {
      setCurrentTemplate({
        ...templates[selectedLang],
        benefits: templates[selectedLang].benefits || []
      });
    }
  }, [selectedLang, templates]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/email-templates?template_type=${templateType}`);
      setTemplates(res.data.templates || {});
      setLanguages(res.data.languages || []);
      
      // Set first template as current
      if (res.data.templates && Object.keys(res.data.templates).length > 0) {
        const firstLang = selectedLang in res.data.templates ? selectedLang : Object.keys(res.data.templates)[0];
        setSelectedLang(firstLang);
        setCurrentTemplate({
          ...res.data.templates[firstLang],
          benefits: res.data.templates[firstLang].benefits || []
        });
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Şablonlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogo = async () => {
    try {
      const res = await api.get('/admin/email-logo');
      setLogoUrl(getEmailLogoDisplayUrl(res.data.logo_url));
      setIsCustomLogo(res.data.is_custom);
    } catch (error) {
      console.error('Failed to fetch logo:', error);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast.error('Sadece PNG, JPG veya WebP dosyaları yüklenebilir');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Dosya boyutu 2MB\'dan küçük olmalıdır');
      return;
    }

    setLogoLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/admin/email-logo/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Logo başarıyla yüklendi');
      if (res.data?.logo_url) {
        setLogoUrl(`${getEmailLogoDisplayUrl(res.data.logo_url)}?t=${Date.now()}`);
        setIsCustomLogo(true);
      } else {
        await fetchLogo();
      }
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Logo yüklenemedi');
    } finally {
      setLogoLoading(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleLogoReset = async () => {
    if (!confirm('Logoyu varsayılana sıfırlamak istediğinizden emin misiniz?')) return;
    
    setLogoLoading(true);
    try {
      await api.delete('/admin/email-logo');
      toast.success('Logo varsayılana sıfırlandı');
      fetchLogo();
    } catch (error) {
      console.error('Failed to reset logo:', error);
      toast.error('Logo sıfırlanamadı');
    } finally {
      setLogoLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/email-templates', {
        template_type: templateType,
        language: selectedLang,
        ...currentTemplate
      });
      toast.success(`${LANGUAGE_NAMES[selectedLang]} şablonu kaydedildi`);
      fetchTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Bu şablonu varsayılana sıfırlamak istediğinizden emin misiniz?')) return;
    
    try {
      await api.post(`/admin/email-templates/reset?template_type=${templateType}&language=${selectedLang}`);
      toast.success('Şablon varsayılana sıfırlandı');
      fetchTemplates();
    } catch (error) {
      console.error('Failed to reset template:', error);
      toast.error('Sıfırlama başarısız');
    }
  };

  const handlePreview = async () => {
    try {
      const endpoint = templateType === 'restaurant' 
        ? '/admin/restaurant-emails/preview'
        : '/admin/market-emails/preview';
      // Send current template data for live preview
      const res = await api.post(`${endpoint}?language=${selectedLang}`, currentTemplate);
      setPreviewHtml(res.data.html);
      setShowPreview(true);
    } catch (error) {
      console.error('Failed to get preview:', error);
      toast.error('Önizleme yüklenemedi');
    }
  };

  const addBenefit = () => {
    setCurrentTemplate(prev => ({
      ...prev,
      benefits: [...(prev.benefits || []), '✅ Yeni fayda']
    }));
  };

  const removeBenefit = (index) => {
    setCurrentTemplate(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const updateBenefit = (index, value) => {
    setCurrentTemplate(prev => ({
      ...prev,
      benefits: prev.benefits.map((b, i) => i === index ? value : b)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6" />
            E-posta Şablon Editörü
          </h2>
          <p className="text-muted-foreground">
            Restoran ve market davet e-postalarını özelleştirin
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handlePreview}>
            <Eye className="w-4 h-4 mr-2" />
            Önizle
          </Button>
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Sıfırla
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Kaydet
          </Button>
        </div>
      </div>

      {/* Logo Management Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Image className="w-5 h-5" />
            E-posta Logosu
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            {/* Logo Preview */}
            <div className="relative">
              <div className="w-32 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                {logoUrl ? (
                  <img 
                    src={logoUrl} 
                    alt="Email Logo" 
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.setAttribute('data-broken', '1');
                    }}
                  />
                ) : (
                  <Image className="w-8 h-8 text-gray-400" />
                )}
              </div>
              {isCustomLogo && (
                <Badge className="absolute -top-2 -right-2 bg-green-500 text-xs">
                  Özel
                </Badge>
              )}
            </div>
            
            {/* Upload Controls */}
            <div className="flex-1 space-y-2">
              <p className="text-sm text-muted-foreground">
                E-posta şablonlarında görünecek logo. PNG, JPG veya WebP formatında, max 2MB.
              </p>
              <div className="flex items-center gap-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={logoLoading}
                  onClick={() => logoInputRef.current?.click()}
                >
                  {logoLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Logo Yükle
                </Button>
                {isCustomLogo && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleLogoReset}
                    disabled={logoLoading}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Varsayılana Dön
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Template Type Tabs */}
      <Tabs value={templateType} onValueChange={setTemplateType}>
        <TabsList>
          <TabsTrigger value="restaurant">🍽️ Restoran Şablonları</TabsTrigger>
          <TabsTrigger value="market">🛒 Market Şablonları</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Language Selector */}
      <div className="flex items-center gap-4">
        <Label className="flex items-center gap-2">
          <Languages className="w-4 h-4" />
          Dil:
        </Label>
        <div className="flex gap-2">
          {languages.map(lang => (
            <Button
              key={lang}
              variant={selectedLang === lang ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedLang(lang)}
            >
              {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
            </Button>
          ))}
        </div>
      </div>

      {/* Editor Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Temel Bilgiler</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Konu Satırı</Label>
              <Input
                value={currentTemplate.subject || ''}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="E-posta konu satırı"
              />
            </div>
            
            <div>
              <Label>Selamlama</Label>
              <Input
                value={currentTemplate.greeting || ''}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, greeting: e.target.value }))}
                placeholder="Sayın {restaurant_name} Yetkilisi,"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {'{restaurant_name}'} veya {'{market_name}'} kullanın
              </p>
            </div>
            
            <div>
              <Label>Giriş Metni</Label>
              <Textarea
                value={currentTemplate.intro || ''}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, intro: e.target.value }))}
                placeholder="Giriş paragrafı"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Slogan 1</Label>
                <Input
                  value={currentTemplate.slogan1 || ''}
                  onChange={(e) => setCurrentTemplate(prev => ({ ...prev, slogan1: e.target.value }))}
                />
              </div>
              <div>
                <Label>Slogan 2</Label>
                <Input
                  value={currentTemplate.slogan2 || ''}
                  onChange={(e) => setCurrentTemplate(prev => ({ ...prev, slogan2: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right Column - Benefits */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Faydalar Listesi</span>
              <Button size="sm" variant="outline" onClick={addBenefit}>
                <Plus className="w-4 h-4 mr-1" />
                Ekle
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Faydalar Başlığı</Label>
              <Input
                value={currentTemplate.benefits_title || ''}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, benefits_title: e.target.value }))}
                placeholder="Neden Turkishemarket?"
              />
            </div>
            
            <div className="space-y-2">
              {(currentTemplate.benefits || []).map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={benefit}
                    onChange={(e) => updateBenefit(index, e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => removeBenefit(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Bottom Left - CTA & Video */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Çağrı Butonu & Video</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Buton Metni</Label>
              <Input
                value={currentTemplate.cta_button || ''}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, cta_button: e.target.value }))}
                placeholder="Aktifleştir"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Tanıtım Videosu Göster</Label>
              <Switch
                checked={currentTemplate.show_video !== false}
                onCheckedChange={(checked) => setCurrentTemplate(prev => ({ ...prev, show_video: checked }))}
              />
            </div>
            
            <div>
              <Label>Video Metni</Label>
              <Input
                value={currentTemplate.video_text || ''}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, video_text: e.target.value }))}
                placeholder="Tanıtım Videomuzu İzleyin"
                disabled={!currentTemplate.show_video}
              />
            </div>
          </CardContent>
        </Card>

        {/* Bottom Right - Contact & Footer */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">İletişim & Alt Bilgi</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>İletişim Metni</Label>
              <Input
                value={currentTemplate.contact_text || ''}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, contact_text: e.target.value }))}
                placeholder="Sorularınız mı var? Bize ulaşın:"
              />
            </div>
            
            <div>
              <Label>İletişim E-postası</Label>
              <Input
                value={currentTemplate.contact_email || ''}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, contact_email: e.target.value }))}
                placeholder="info@turkishemarket.com"
              />
            </div>
            
            <div>
              <Label>Alt Bilgi</Label>
              <Textarea
                value={currentTemplate.footer || ''}
                onChange={(e) => setCurrentTemplate(prev => ({ ...prev, footer: e.target.value }))}
                placeholder="Turkishemarket Ailesi"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>E-posta Önizleme - {LANGUAGE_NAMES[selectedLang]}</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden">
            <iframe
              srcDoc={previewHtml}
              className="w-full h-[600px]"
              title="Email Preview"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplateEditor;
